/**
 * Client-side account deletion: user tasks, preferences/settings, then Firebase Auth user.
 */
import {
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  doc,
  deleteDoc,
  limit,
} from 'firebase/firestore';
import { deleteUser } from 'firebase/auth';
import { db, auth } from '@/lib/firebase';

/** Firestore write batch size limit (tasks deleted per commit). */
export const ACCOUNT_DELETE_BATCH_SIZE = 500;

export const ACCOUNT_DELETE_AUTH_USER_FAILED = 'account-delete/auth-user-failed';

/**
 * @param {string} userId
 */
export async function deleteUserTasksForAccount(userId) {
  const tasksQuery = query(
    collection(db, 'tasks'),
    where('userId', '==', userId),
    limit(ACCOUNT_DELETE_BATCH_SIZE),
  );

  while (true) {
    const snapshot = await getDocs(tasksQuery);
    if (snapshot.docs.length === 0) {
      return;
    }

    const batch = writeBatch(db);
    snapshot.docs.forEach((taskDoc) => {
      batch.delete(taskDoc.ref);
    });
    await batch.commit();

    if (snapshot.docs.length < ACCOUNT_DELETE_BATCH_SIZE) {
      return;
    }
  }
}

/**
 * @param {string} userId
 */
export async function deleteUserPreferencesSettingsDoc(userId) {
  const prefRef = doc(db, 'users', userId, 'preferences', 'settings');
  await deleteDoc(prefRef);
}

/**
 * @param {string} userId
 */
export async function deleteUserFirestoreData(userId) {
  await deleteUserTasksForAccount(userId);
  await deleteUserPreferencesSettingsDoc(userId);
}

export async function deleteFirebaseAuthUser() {
  const user = auth.currentUser;
  if (!user) {
    const err = new Error('No authenticated user');
    err.code = 'auth/no-user';
    throw err;
  }

  try {
    await deleteUser(user);
  } catch (error) {
    const wrapped = new Error('Firebase Auth user deletion failed after data removal');
    wrapped.code = ACCOUNT_DELETE_AUTH_USER_FAILED;
    wrapped.cause = error;
    throw wrapped;
  }
}

/**
 * @param {string} userId
 */
export async function executeAccountDeletion(userId) {
  await deleteUserFirestoreData(userId);
  await deleteFirebaseAuthUser();
}
